import { takeRight } from 'lodash';
import React, { useContext, useEffect, useMemo } from 'react';
import { UseQueryResult, useQuery } from 'react-query';

import { sciDomainsApi } from 'api';
import { LanguageCode, QueryKey, SciTemplate } from 'enums';
import { useLanguage } from 'hooks';
import { SciDomain } from 'types';

export const SciDomainContext = React.createContext<UseQueryResult<
  SciDomain | undefined
> | null>(null);

export const SciDomainProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [orderId, token] = takeRight(window.location.pathname.split('/'), 2);
  const { changeLanguage } = useLanguage();

  const queryResultSciDomain = useQuery(
    QueryKey.SciDomains,
    () => sciDomainsApi.getOneForOrder(orderId!, token!),
    {
      enabled: !!orderId && !!token,
      refetchOnWindowFocus: false,
      refetchInterval: 0,
      retry: false,
    },
  );

  const contextValue = useMemo(
    () => queryResultSciDomain,
    [queryResultSciDomain],
  );

  useEffect(() => {
    const template = queryResultSciDomain.data?.template;
    if (template === SciTemplate.SQ || template === SciTemplate.SberQr) {
      changeLanguage(LanguageCode.Russian);
    }
  }, [queryResultSciDomain.data?.template, changeLanguage]);

  return (
    <SciDomainContext.Provider value={contextValue}>
      {children}
    </SciDomainContext.Provider>
  );
};

export const useSciDomain = () => {
  const context = useContext(SciDomainContext);
  if (context === null) {
    throw new Error('useSciDomain must be used within a SciDomainProvider');
  }

  return context;
};
